import { selectBasket, sendBasket, updatePaymentInProgress } from 'containers/OrderDetails/basketSlice'
import { addNotification } from 'containers/Notifications/notificationsSlice'
import { selectIsLoggedIn, selectUserId } from 'containers/Login/loginSlice'
import { useHistory, useLocation } from 'react-router-dom'
import useBasketFinalize from 'hooks/useBasketFinalize'
import { useDispatch, useSelector } from 'react-redux'
import { LOCATION_STATES } from 'utils/constants'
import { useEffect, useRef } from 'react'
import { v7 } from 'uuid'
import {
  SAVED_METHOD_POLL_TIMEOUT,
  awaitStatus,
  buildTokenExtra,
  getMerchantLabel,
  getWalletAdapter,
  hasCardAdapter,
  isWalletNative,
  resolveWalletPosId,
  resolveWalletSandbox,
  settle,
  toAmount
} from 'payments'

/**
 * Płatność w koszyku. Kolejność jest zawsze ta sama:
 *   newDocument -> (token karty / portfela, jeśli metoda go wymaga) -> settlement -> url albo status.
 *
 * `selected` to cała metoda z checkout.payments (plus `methodId` i zgoda `saveForRecurring`),
 * więc rozgałęziamy się po `key` i po polach `cardForm` / `walletForm` - nigdy po nazwie operatora.
 */
const usePaymentProcessing = (selected, isLoading, setLoading) => {
  const isLoggedIn = useSelector(selectIsLoggedIn)
  const { cartId, checkout, invoice, newDocuments } = useSelector(selectBasket)
  const userId = useSelector(selectUserId)

  const isActive = useRef(false)
  const currentIsLoggedIn = useRef(isLoggedIn)
  currentIsLoggedIn.current = isLoggedIn

  const dispatch = useDispatch()
  const { pathname } = useLocation()
  const { replace, push } = useHistory()
  const { finalizeBasket } = useBasketFinalize()

  useEffect(() => {
    return () => {
      isActive.current = false
    }
  }, [])

  const getPayment = (newDocumentsResponse) => {
    const payment = {
      amount: (newDocumentsResponse?.checkout || checkout)?.to_pay?.now?.grossPrice,
      methodId: selected.methodId,
      key: selected.key,
      date: 'now'
    }
    if (selected.optionId) {
      payment.optionId = selected.optionId
    }
    return payment
  }

  const callback = (payment) => {
    if (payment) {
      dispatch(sendBasket({
        step: 'finalize',
        payments: payment === 'none' ? undefined : [payment]
      })).then(res => {
        if (res.payload?.isFinished || res.payload?.sold) {
          finalizeBasket()

        } else if (res.payload?.locked && payment.remotePaymentId) {
          replace(`/payment/status?paymentId=${payment.remotePaymentId}`)

        } else if (isActive.current) {
          callback(payment)
        }
      })

    } else {
      setLoading(false)
    }
  }

  /**
   * @param {string} localPaymentId
   * @param {object|string} extra dodatkowe pola pozycji wpłaty: { code } | { cardToken } | { walletToken }
   */
  const generateBody = (localPaymentId, extra, newDocumentsResponse) => {
    const body = {
      userId,
      cartId,
      action: 'payment',
      invoices: (newDocumentsResponse?.newDocuments || newDocuments).map(({ id, toPay, object }) => ({ invoiceId: id, amount: toPay, object }))
    }

    if (invoice) {
      body.companyNIP = invoice.nip
    }

    const payment = getPayment(newDocumentsResponse)
    payment.localPaymentId = localPaymentId
    payment.init = true

    if (selected.supportsRecurring && selected.saveForRecurring) {
      payment.saveForRecurring = true
    }

    // kompatybilność wstecz: wcześniej drugim argumentem był sam kod BLIK
    const additional = typeof extra === 'string'
      ? (extra ? { code: extra } : {})
      : (extra || {})
    Object.assign(payment, additional)

    body.payments = [payment]

    return body
  }

  /** Płatność się nie udała albo klient ją przerwał - wznawiamy licznik koszyka. */
  const stopPayment = (message, handled) => {
    dispatch(updatePaymentInProgress(false))
    setLoading(false)
    if (message && !handled) {
      dispatch(addNotification({ type: 'error', message, translate: false }))
    }
  }

  /** Jedna reakcja na wynik rozliczenia - taka sama dla karty, portfela, BLIK-a i zwykłego online. */
  const handleResult = (result, localPaymentId, payment) => {
    if (!result) {
      stopPayment()
      return false
    }

    switch (result.type) {
      case 'success':
        callback({ ...payment, localPaymentId, remotePaymentId: result.remotePaymentId })
        return true

      case 'redirect':
        // paymentInProgress jest już ustawiony - bez tego ekran statusu nie sfinalizuje koszyka
        window.open(result.url, '_self')
        setTimeout(() => {
          setLoading(false)
        }, 500)
        return true

      case 'pending':
        // wpłata przyjęta, operator jeszcze nie potwierdził - dalej pyta ekran statusu
        replace(`/payment/status?paymentId=${result.remotePaymentId}`)
        return true

      default:
        stopPayment(result.message, result.handled)
        return false
    }
  }

  const initOnlinePayment = async (newDocumentsResponse) => {
    const localPaymentId = v7()
    const body = generateBody(localPaymentId, {}, newDocumentsResponse)
    if (!body) return false

    // zapisany alias BLIK potrafi czekać na bank do 72 h - nie trzymamy klienta w spinnerze,
    // po chwili oddajemy go ekranowi statusu, który pyta dalej we własnym tempie
    const options = selected.key === 'saved_method'
      ? { timeoutMs: SAVED_METHOD_POLL_TIMEOUT }
      : undefined

    const result = await settle(body, options)
    return handleResult(result, localPaymentId, getPayment(newDocumentsResponse))
  }

  /** Apple Pay / Google Pay w trybie native - arkusz portfela otwieramy u siebie. */
  const payWithWallet = async (newDocumentsResponse) => {
    const walletForm = selected.walletForm
    const adapter = getWalletAdapter(walletForm)
    if (!adapter) {
      stopPayment()
      return false
    }

    const localPaymentId = v7()
    const payment = getPayment(newDocumentsResponse)
    const options = {
      amount: toAmount(payment.amount),
      label: getMerchantLabel(),
      sandbox: resolveWalletSandbox(walletForm, checkout?.payments),
      posId: resolveWalletPosId(walletForm, checkout?.payments)
    }

    try {
      if (walletForm.wallet === 'applepay') {
        let settled = null
        await adapter.requestToken(walletForm, {
          ...options,
          // arkusz Apple czeka na naszą odpowiedź, więc w środku leci samo rozliczenie (bez pollingu)
          onAuthorized: async (token) => {
            const body = generateBody(localPaymentId, buildTokenExtra(selected, token), newDocumentsResponse)
            settled = await settle(body, { poll: false })
            return settled.type !== 'error' && settled.type !== 'failure'
          }
        })

        if (settled?.type === 'pending') {
          settled = await awaitStatus(settled.remotePaymentId)
        }
        return handleResult(settled, localPaymentId, payment)
      }

      const token = await adapter.requestToken(walletForm, options)
      const body = generateBody(localPaymentId, buildTokenExtra(selected, token), newDocumentsResponse)
      const result = await settle(body)
      return handleResult(result, localPaymentId, payment)

    } catch (error) {
      // token portfela jest jednorazowy - po błędzie klient musi otworzyć arkusz od nowa
      if (error?.code === 'wallet_cancelled') {
        stopPayment()
        return false
      }
      console.error(error)
      stopPayment(error?.message && error.message !== error?.code ? error.message : '')
      return false
    }
  }

  const purchase = async () => {
    if (isLoading) return
    setLoading(true)
    const payment = getPayment()

    dispatch(sendBasket({ step: 'newDocument', payments: [payment] })).then(res => {
      if (!res.payload?.newDocuments) {
        setLoading(false)
        return
      }

      // od tego momentu licznik koszyka stoi - płatność bywa dłuższa niż czas rezerwacji
      dispatch(updatePaymentInProgress(true))

      switch (selected.key) {
        case 'blik':
          push(pathname, LOCATION_STATES.popup({ type: 'blik', payment }))
          break

        case 'online_card':
          if (hasCardAdapter(selected.cardForm)) {
            push(pathname, LOCATION_STATES.popup({
              type: 'card',
              payment,
              cardForm: selected.cardForm,
              saveForRecurring: !!(selected.supportsRecurring && selected.saveForRecurring)
            }))
          } else {
            initOnlinePayment(res.payload)
          }
          break

        case 'applepay':
        case 'gpay':
          if (isWalletNative(selected)) {
            payWithWallet(res.payload)
          } else {
            initOnlinePayment(res.payload)
          }
          break

        default:
          initOnlinePayment(res.payload)
      }
    })
  }

  return {
    purchase,
    generateBody,
    handleResult,
    stopPayment,
    callback
  }
}

export default usePaymentProcessing
